import { useMsal } from "@azure/msal-react";
import type React from "react";
import { createContext, useContext, useEffect, useMemo, useRef, useState } from "react";
import { fetchUserPermissions } from "@/api/permissions";
import { useApiToken } from "@/auth/useApiToken";
import { features } from "@/config";
import type { LaboratoryPermissionType } from "@/contracts/permissions";
import { AllowAllPermissionService, type IPermissionService, PermissionService } from "@/services/PermissionService";

const PermissionContext = createContext<IPermissionService | null>(null);

export const PermissionProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { accounts } = useMsal();
  const getApiToken = useApiToken();
  const getApiTokenRef = useRef(getApiToken);
  getApiTokenRef.current = getApiToken;
  const email = accounts[0]?.username;

  const [isUserAdmin, setIsUserAdmin] = useState(false);
  const [labPermissions, setLabPermissions] = useState<Map<string, Set<LaboratoryPermissionType>>>(new Map());
  const [isLoaded, setIsLoaded] = useState(false);

  useEffect(() => {
    if (!features.permissions || !email) {
      setIsLoaded(true);
      return;
    }

    let cancelled = false;
    getApiTokenRef.current().then(async (token) => {
      const data = await fetchUserPermissions(token, email);
      if (cancelled) return;
      setIsUserAdmin(data.isUserAdmin);
      setLabPermissions(new Map(data.laboratoryPermissions.map((l) => [l.laboratoryId, new Set(l.permissions)])));
      setIsLoaded(true);
    });

    return () => {
      cancelled = true;
    };
  }, [email]);

  const service = useMemo<IPermissionService>(() => {
    if (!features.permissions) return new AllowAllPermissionService();
    return new PermissionService(isUserAdmin, labPermissions, isLoaded);
  }, [isUserAdmin, labPermissions, isLoaded]);

  return <PermissionContext.Provider value={service}>{children}</PermissionContext.Provider>;
};

export const usePermissions = (): IPermissionService => {
  const ctx = useContext(PermissionContext);
  if (!ctx) throw new Error("usePermissions must be used within PermissionProvider");
  return ctx;
};
